// ApplicationsTab.jsx
//
// Об'єднана вкладка "Заявки на вступ": райони як основний рівень, з
// розгортанням списку садочків усередині рядка району (раніше — окрема
// вкладка "Заявки в розрізі садочків" / EnrollmentTab.jsx). Дані по районах
// і по садочках вантажаться одночасно одним набором фільтрів, щоб цифри
// узгоджувались між рівнями.
import React, { useEffect, useState, useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import { useApplicationsData, useApplicationsByKindergartenData } from './hooks/useStatisticsData';
import { useDragScroll } from './hooks/useDragScroll';
import { useIsMobile } from './hooks/useIsMobile';
import { resolveBenefitsParam } from './statisticsApi';
import KindergartenBreakdownTable from './KindergartenBreakdownTable';
import MobileSheet from './MobileSheet';
import { externalTooltipHandler } from './utils/externalTooltip';
import './chartConfig'; // Импортируем конфигурацию

// Спеціальний "ключ району" для розгорнутого рядка "По Києву" — показує
// садочки всього міста, а не одного району, тому позначаємо його окремо
// від реальних назв районів.
const CITY_ALL_KEY = '__all_kyiv__';

const ApplicationsTab = ({ filters, onLoading, onError }) => {
  const {
    districtData, totalData,
    loading: loadingDistrict, error: errorDistrict, fetchData: fetchDistrictData,
  } = useApplicationsData();
  const {
    data: kgData,
    loading: loadingKg, error: errorKg, fetchData: fetchKgData,
  } = useApplicationsByKindergartenData();

  const [tableData, setTableData] = useState([]);
  const [totalRow, setTotalRow] = useState(null);
  const [openDistrict, setOpenDistrict] = useState(null);

  const { ref: scrollRef, listeners: dragListeners } = useDragScroll();
  const isMobile = useIsMobile();

  const loading = loadingDistrict || loadingKg;
  const error = errorDistrict || errorKg;

  useEffect(() => {
    if (filters.year) {
      const yearNum = parseInt(filters.year.split('→')[0].trim());
      const apiFilters = {
        kyiv_reg: filters.registration === 'kyiv_reg' ? 'kyiv_reg' : 'all',
        kyiv_mr: filters.municipalCard === 'kyiv_mr_parent' ? 'kyiv_mr_parent' : 'all',
        benefits: resolveBenefitsParam(filters.benefit),
        reserv: filters.invitations || 'all',
        in_kg: filters.enrolled || 'all',
        req_crt: filters.created || 'all',
        with_move: filters.moved == "with_move" ? "with_move" : 'all',
        without_move: filters.moved == "without_move" ? "without_move" : 'all'
      };
      fetchDistrictData(yearNum, apiFilters);
      // Той самий набір фільтрів (раніше тут бракувало with_move/without_move,
      // через що фільтр "Переміщені" міг розходитись між таблицями).
      fetchKgData(yearNum, apiFilters);
    }
  }, [filters, fetchDistrictData, fetchKgData]);

  useEffect(() => {
    if (onLoading) onLoading(loading);
  }, [loading, onLoading]);

  useEffect(() => {
    if (onError && error) onError(error);
  }, [error, onError]);

  useEffect(() => {
    if (!districtData || !totalData) {
      setTableData([]);
      setTotalRow(null);
      return;
    }
    {
      const rows = [];

      // Сортируем районы
      const sortedDistricts = Object.keys(districtData).sort();

      sortedDistricts.forEach(district => {
        const ages = districtData[district];
        const row = {
          district,
          age0_1: ages[1970] || 0,
          age1_2: ages[1971] || 0,
          age2_3: ages[1972] || 0,
          age3_4: ages[1973] || 0,
          age4_5: ages[1974] || 0,
          age5_6: ages[1975] || 0,
          age6_7: ages[1976] || 0,
          age7_8: ages[1977] || 0,
        };
        row.total = row.age0_1 + row.age1_2 + row.age2_3 + row.age3_4 + row.age4_5 + row.age5_6 + row.age6_7 + row.age7_8;
        row.total3_6 = row.age3_4 + row.age4_5 + row.age5_6;
        rows.push(row);
      });

      // Формируем строку итогов
      const totals = {
        district: 'По Києву',
        age0_1: totalData[1970] || 0,
        age1_2: totalData[1971] || 0,
        age2_3: totalData[1972] || 0,
        age3_4: totalData[1973] || 0,
        age4_5: totalData[1974] || 0,
        age5_6: totalData[1975] || 0,
        age6_7: totalData[1976] || 0,
        age7_8: totalData[1977] || 0,
      };
      totals.total = totals.age0_1 + totals.age1_2 + totals.age2_3 + totals.age3_4 + totals.age4_5 + totals.age5_6 + totals.age6_7 + totals.age7_8;
      totals.total3_6 = totals.age3_4 + totals.age4_5 + totals.age5_6;

      setTableData(rows);
      setTotalRow(totals);
    }
  }, [districtData, totalData]);

  // Рядки по садочках (district вже нормалізований у хуку) — групуємо по
  // району, щоб миттєво віддавати підмножину при розгортанні рядка.
  const kgRowsByDistrict = useMemo(() => {
    const groups = {};
    if (!kgData) return groups;

    Object.keys(kgData).forEach(kgId => {
      const kg = kgData[kgId];
      const row = {
        id: kgId,
        kgnumber: kg.kgnumber,
        district: kg.distr,
        age0_1: kg[1970] || 0,
        age1_2: kg[1971] || 0,
        age2_3: kg[1972] || 0,
        age3_4: kg[1973] || 0,
        age4_5: kg[1974] || 0,
        age5_6: kg[1975] || 0,
        age6_7: kg[1976] || 0,
        age7_8: kg[1977] || 0,
      };
      row.total = row.age0_1 + row.age1_2 + row.age2_3 + row.age3_4 + row.age4_5 + row.age5_6 + row.age6_7 + row.age7_8;
      row.total3_6 = row.age3_4 + row.age4_5 + row.age5_6;

      if (!groups[row.district]) groups[row.district] = [];
      groups[row.district].push(row);
    });

    return groups;
  }, [kgData]);

  // Плаский список усіх садочків міста — для розгортання рядка "По Києву"
  const allKgRows = useMemo(
    () => Object.values(kgRowsByDistrict).flat(),
    [kgRowsByDistrict]
  );

  const pieData = {
    labels: ['До року', '1-2 роки', '2-3 роки', '3-4 роки', '4-5 років', '5-6 років', '6-7 років', '7-8 років'],
    datasets: [{
      data: totalRow ? [
        totalRow.age0_1, totalRow.age1_2, totalRow.age2_3, totalRow.age3_4,
        totalRow.age4_5, totalRow.age5_6, totalRow.age6_7, totalRow.age7_8
      ] : [0, 0, 0, 0, 0, 0, 0, 0],
      backgroundColor: ['#c39bd3', '#e8a898', '#f4d03f', '#e74c3c', '#58d68d', '#a9dfbf', '#f7dc6f', '#3498db'],
      borderWidth: 0,
    }],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false, external: externalTooltipHandler, callbacks: { label: (context) => `${context.label}: ${context.raw} заявок` } }
    },
    cutout: '60%',
  };

  if (loading && !tableData.length) {
    return <div className="text-center py-5">Завантаження даних...</div>;
  }

  if (error && !tableData.length) {
    return <div className="text-center py-5 text-danger">Помилка: {error}</div>;
  }

  if (!tableData.length && !loading) {
    return <div className="text-center py-5 text-muted">Немає даних за обраними параметрами</div>;
  }

  return (
    <>
      <div className="publicstatistic-statistics-table-section">
        <h3 className="publicstatistic-section-title">
          Заявки на вступ
        </h3>
        <div className="mb-2" style={{ fontSize: '0.82rem', color: '#666' }}>
          Натисніть на «По Києву» або на район, щоб побачити розподіл по садочках
        </div>
        <div
          ref={scrollRef}
          className="publicstatistic-table-responsive"
          style={{ cursor: 'grab', overflowX: 'auto' }}
          {...dragListeners}
          >
          <table id="pubstat-applications-table" className="table publicstatistic-statistics-table">
            <thead>
              <tr>
                <th>Район</th>
                <th>Разом</th>
                <th>Разом 3-6</th>
                <th>До року</th>
                <th>1-2 роки</th>
                <th>2-3 роки</th>
                <th>3-4 роки</th>
                <th>4-5 років</th>
                <th>5-6 років</th>
                <th>6-7 років</th>
                <th>7-8 років</th>
              </tr>
            </thead>
            <tbody>
              {totalRow && (
                <React.Fragment>
                  <tr
                    onClick={() => setOpenDistrict(prev => prev === CITY_ALL_KEY ? null : CITY_ALL_KEY)}
                    style={{
                      background: '#eef2ff',
                      borderBottom: '2px solid #c7d2fe',
                      cursor: 'pointer',
                      borderLeft: openDistrict === CITY_ALL_KEY ? '3px solid #0069ff' : '3px solid transparent',
                    }}
                    title="Натисніть для перегляду всіх садочків міста"
                  >
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'left', verticalAlign: 'middle', fontSize: '0.875rem' }}>
                      <span style={{ marginRight: 4, fontSize: '0.7rem', color: openDistrict === CITY_ALL_KEY ? '#0069ff' : '#6b82c4' }}>
                        {openDistrict === CITY_ALL_KEY ? '▼' : '▶'}
                      </span>
                      <strong>{totalRow.district}</strong>
                    </td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.total.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.total3_6.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.age0_1.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.age1_2.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.age2_3.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.age3_4.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.age4_5.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.age5_6.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.age6_7.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{totalRow.age7_8.toLocaleString()}</strong></td>
                  </tr>
                  {openDistrict === CITY_ALL_KEY && !isMobile && (
                    <tr>
                      <td colSpan={11} style={{ padding: 0, background: '#fafbff', borderLeft: '3px solid #0069ff' }}>
                        <KindergartenBreakdownTable
                          rows={allKgRows}
                          filters={filters}
                          loading={loadingKg && !kgData}
                          showDistrict
                          scopeLabel="місті"
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )}
              {tableData.map((row, index) => (
                <React.Fragment key={row.district}>
                  <tr
                    className={index % 2 === 0 ? 'publicstatistic-row-even' : ''}
                    onClick={() => setOpenDistrict(prev => prev === row.district ? null : row.district)}
                    style={{
                      cursor: 'pointer',
                      borderLeft: openDistrict === row.district ? '3px solid #0069ff' : '3px solid transparent',
                      background: openDistrict === row.district ? '#f0f4ff' : undefined,
                    }}
                    title="Натисніть для перегляду садочків району"
                  >
                    <td className="publicstatistic-district-name">
                      <span style={{ marginRight: 4, fontSize: '0.7rem', color: openDistrict === row.district ? '#0069ff' : '#9ca3af' }}>
                        {openDistrict === row.district ? '▼' : '▶'}
                      </span>
                      {row.district}
                    </td>
                    <td className="publicstatistic-total-cell">{row.total.toLocaleString()}</td>
                    <td className="publicstatistic-total-cell">{row.total3_6.toLocaleString()}</td>
                    <td>{row.age0_1.toLocaleString()}</td>
                    <td>{row.age1_2.toLocaleString()}</td>
                    <td>{row.age2_3.toLocaleString()}</td>
                    <td>{row.age3_4.toLocaleString()}</td>
                    <td>{row.age4_5.toLocaleString()}</td>
                    <td>{row.age5_6.toLocaleString()}</td>
                    <td>{row.age6_7.toLocaleString()}</td>
                    <td>{row.age7_8.toLocaleString()}</td>
                  </tr>
                  {openDistrict === row.district && !isMobile && (
                    <tr>
                      <td colSpan={11} style={{ padding: 0, background: '#fafbff', borderLeft: '3px solid #0069ff' }}>
                        <KindergartenBreakdownTable
                          rows={kgRowsByDistrict[row.district] || []}
                          filters={filters}
                          loading={loadingKg && !kgData}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

            </tbody>
          </table>
        </div>
      </div>

      {isMobile && (
        <MobileSheet
          isOpen={!!openDistrict}
          onClose={() => setOpenDistrict(null)}
          title={openDistrict === CITY_ALL_KEY ? 'Всі садочки міста' : 'Садочки району'}
          subtitle={openDistrict === CITY_ALL_KEY ? undefined : openDistrict}
        >
          <KindergartenBreakdownTable
            rows={openDistrict === CITY_ALL_KEY ? allKgRows : (kgRowsByDistrict[openDistrict] || [])}
            filters={filters}
            loading={loadingKg && !kgData}
            showDistrict={openDistrict === CITY_ALL_KEY}
            scopeLabel={openDistrict === CITY_ALL_KEY ? 'місті' : 'районі'}
            capHeight={false}
          />
        </MobileSheet>
      )}

      <div className="publicstatistic-charts-section">
        <div className="row">
          <div className="col-lg-6 offset-lg-3">
            <h4 className="publicstatistic-chart-title">Розподіл заявок за віком</h4>
            <div className="publicstatistic-custom-pie-chart">
              <div className="publicstatistic-pie-chart-center">
                <span className="publicstatistic-chart-total">{totalRow?.total.toLocaleString() || 0}</span>
                <span className="publicstatistic-chart-total-label">всього заявок</span>
              </div>
              <Pie data={pieData} options={pieOptions} />
            </div>
            <div className="publicstatistic-chart-legend">
              {pieData.labels.map((label, index) => (
                <div key={index} className="publicstatistic-legend-item">
                  <span className="publicstatistic-legend-color" style={{ backgroundColor: pieData.datasets[0].backgroundColor[index] }}></span>
                  <span>{label}: {pieData.datasets[0].data[index].toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationsTab;